import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { BluCloudThemeProvider, BluDataGridPremium } from '@bludata/components';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useState, useEffect } from 'react';
import { ordiniAcquisto_interface } from '../interfaces/ordiniAcquisto_interface';

interface DialogAsyncListaAvvisiLacProps {
  listaOrdini: ordiniAcquisto_interface[];
  onClose: (result: ordiniAcquisto_interface[]) => void;
}

export default function DialogAsyncListaOrdini({
  listaOrdini,
  onClose,
}: DialogAsyncListaAvvisiLacProps) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set(),
    type: 'include',
  });

  const [listaOrdiniTmp, setListaOrdiniTmp] =
    useState<ordiniAcquisto_interface[]>(listaOrdini);

  // Funzione per calcolare la quantità massima ordinabile
  const calcolaQuantitaMaxOrdinabile = (ordine: ordiniAcquisto_interface) => {
    return (
      ordine.quantitaOrdine -
      (ordine.quantitaConsegnata + ordine.quantitaArticolo)
    );
  };

  // Funzione helper per verificare se una riga è selezionata
  const isRowSelected = (rowId: any) => {
    if (selectionModel.type === 'exclude' && selectionModel.ids.size === 0) {
      // Caso "seleziona tutti"
      return true;
    } else if (selectionModel.type === 'include') {
      // Modalità normale - solo quelle nell'ids sono selezionate
      return selectionModel.ids.has(rowId);
    } else {
      // Modalità exclude - tutte sono selezionate eccetto quelle nell'ids
      return !selectionModel.ids.has(rowId);
    }
  };

  // Calcola il totale degli articoli da caricare (somma qta acq delle righe selezionate)
  const calcolaTotaleArticoliDaCaricare = () => {
    return listaOrdiniTmp.reduce((total, ordine) => {
      const isSelected = isRowSelected(ordine.codiceFilialeOrdineRiga);
      return isSelected ? total + (ordine.quantita || 0) : total;
    }, 0);
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'data',
      headerName: 'Data ordine',
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
      valueGetter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'riferimento',
      headerName: 'Riferimento',
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
    },
    {
      field: 'quantitaArticolo',
      headerName: 'Qta articolo',
      align: 'center',
      headerAlign: 'center',
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
    },
    {
      field: 'quantitaOrdine',
      headerName: 'Qta ordine',
      align: 'center',
      headerAlign: 'center',
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
    },
    {
      field: 'quantitaConsegnata',
      headerName: 'Qta consegnata',
      align: 'center',
      headerAlign: 'center',
      width: 120,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
    },
    {
      field: 'quantita',
      headerName: 'Qta acq',
      align: 'center',
      headerAlign: 'center',
      width: 90,
      disableColumnMenu: true,
      sortable: false,
      editable: true,
      type: 'number',
      preProcessEditCellProps: (params) => {
        const ordine = listaOrdiniTmp.find(
          (o) => o.codiceFilialeOrdineRiga === params.id,
        );
        if (!ordine) return { ...params.props, error: true };

        const maxValue = calcolaQuantitaMaxOrdinabile(ordine);
        let value = Number(params.props.value);

        // Se il valore supera il massimo, imposta automaticamente al massimo
        if (!isNaN(value) && value > maxValue) {
          value = maxValue;
          params.props.value = value;
        }

        // Verifica che il valore sia valido (maggiore o uguale a zero)
        const isValid = !isNaN(value) && value >= 0;

        return { ...params.props, value, error: !isValid };
      },
      cellClassName: (params) => {
        return isRowSelected(params.id) ? 'editable-cell' : '';
      },
    },
  ];

  const themeMode =
    window.document.querySelector('html')?.getAttribute('dark-mode') === 'true'
      ? 'dark'
      : 'light';

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectionModel(newSelection);

    // Se la selezione è vuota ma il tipo è 'exclude', significa che sono tutti selezionati
    const isSelectAll =
      newSelection.type === 'exclude' && newSelection.ids.size === 0;

    const updatedListaOrdini: ordiniAcquisto_interface[] = listaOrdiniTmp.map(
      (ordine) => {
        let isCurrentlySelected: boolean;

        if (isSelectAll) {
          // Se è "seleziona tutti", tutte le righe sono selezionate
          isCurrentlySelected = true;
        } else if (newSelection.type === 'include') {
          // Modalità normale - solo quelle nell'ids sono selezionate
          isCurrentlySelected = newSelection.ids.has(
            ordine.codiceFilialeOrdineRiga,
          );
        } else {
          // Modalità exclude - tutte sono selezionate eccetto quelle nell'ids
          isCurrentlySelected = !newSelection.ids.has(
            ordine.codiceFilialeOrdineRiga,
          );
        }

        const wasSelected =
          selectionModel.type === 'exclude'
            ? selectionModel.ids.size === 0 ||
              !selectionModel.ids.has(ordine.codiceFilialeOrdineRiga)
            : selectionModel.ids.has(ordine.codiceFilialeOrdineRiga);

        if (isCurrentlySelected) {
          // Se è appena stato selezionato o ha quantità 0, calcola il massimo
          if (!wasSelected || ordine.quantita === 0) {
            return {
              ...ordine,
              quantita: calcolaQuantitaMaxOrdinabile(ordine),
            };
          }
          // Se era già selezionato e ha una quantità > 0, mantienila
          return ordine;
        } else {
          // Se non è selezionato, azzera la quantità
          return {
            ...ordine,
            quantita: 0,
          };
        }
      },
    );
    setListaOrdiniTmp(updatedListaOrdini);
  };

  const handleSend = () => {
    // Gestisci correttamente la selezione finale considerando il tipo
    let ordiniSelezionati: ordiniAcquisto_interface[];

    if (selectionModel.type === 'exclude' && selectionModel.ids.size === 0) {
      // Tutti selezionati
      ordiniSelezionati = listaOrdiniTmp.filter(
        (ordine) => ordine.quantita > 0,
      );
    } else if (selectionModel.type === 'include') {
      // Solo quelli specificati nell'ids
      ordiniSelezionati = listaOrdiniTmp.filter((ordine) =>
        selectionModel.ids.has(ordine.codiceFilialeOrdineRiga),
      );
    } else {
      // Tutti eccetto quelli nell'ids
      ordiniSelezionati = listaOrdiniTmp.filter(
        (ordine) =>
          !selectionModel.ids.has(ordine.codiceFilialeOrdineRiga) &&
          ordine.quantita > 0,
      );
    }

    onClose(ordiniSelezionati);
  };

  return (
    <BluCloudThemeProvider themeMode={themeMode}>
      <Dialog open={true} fullWidth maxWidth="md" fullScreen={false}>
        <DialogTitle>Ordini</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: 350 }}
        >
          <Typography>
            ATTENZIONE: L'articolo selezionato è presente in più ordini!
          </Typography>
          <Typography>
            Seleziona gli ordini in cui vuoi inserire la quantità dell'articolo.
            Dopo aver selezionato gli ordini, puoi modificare la quantità
            acquistata facendo doppio click sul campo
          </Typography>
          <Stack direction="column" spacing={1} flex={1}>
            <BluDataGridPremium
              rows={listaOrdiniTmp}
              columns={gridColumns}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={handleSelectionChange}
              getRowId={(row) => row.codiceFilialeOrdineRiga}
              rowSelectionModel={selectionModel}
              hideFooterSelectedRowCount
              hideFooter={false}
              processRowUpdate={(updatedRow, originalRow) => {
                // Assicuriamoci che il valore non superi il massimo
                const maxValue = calcolaQuantitaMaxOrdinabile(updatedRow);
                if (updatedRow.quantita > maxValue) {
                  updatedRow.quantita = maxValue;
                }

                const newList = listaOrdiniTmp.map((item) =>
                  item.codiceFilialeOrdineRiga ===
                  updatedRow.codiceFilialeOrdineRiga
                    ? updatedRow
                    : item,
                );
                setListaOrdiniTmp(newList);
                return updatedRow;
              }}
              isCellEditable={(params) => {
                return params.field === 'quantita' && isRowSelected(params.id);
              }}
              sx={{
                '& .editable-cell': {
                  backgroundColor: 'rgba(0, 100, 255, 0.1)',
                },
              }}
              slots={{
                footer: () => (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      padding: '8px 16px',
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      Totale articoli selezionati per il carico:{' '}
                      {calcolaTotaleArticoliDaCaricare()}
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{ justifyContent: 'center' }}
            onClick={() => onClose(null)}
          >
            Annulla
          </Button>
          <Button
            variant="outlined"
            sx={{ justifyContent: 'center' }}
            onClick={handleSend}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </BluCloudThemeProvider>
  );
}
